// Currency configuration utility

export interface CurrencyConfig {
  code: string; // ISO currency code (USD, BRL, EUR, etc.)
  symbol: string; // Currency symbol ($, R$, €, etc.)
  locale: string; // Locale for formatting (en-US, pt-BR, etc.)
  name: string; // Full currency name
  paymentProvider: string; // Payment provider config (mercadopago, stripe, etc.)
}

// Currency configurations
const CURRENCY_CONFIGS: Record<string, CurrencyConfig> = {
  USD: {
    code: "USD",
    symbol: "$",
    locale: "en-US",
    name: "US Dollar",
    paymentProvider: "stripe",
  },
  BRL: {
    code: "BRL",
    symbol: "R$",
    locale: "pt-BR",
    name: "Brazilian Real",
    paymentProvider: "mercadopago",
  },
  EUR: {
    code: "EUR",
    symbol: "€",
    locale: "pt-PT", // Mercado alvo do euro hoje é Portugal (24,90 €)
    name: "Euro",
    paymentProvider: "stripe",
  },
};

// Get current currency from environment variable
export function getCurrentCurrency(): string {
  return process.env.NEXT_PUBLIC_CURRENCY || "BRL";
}

// Get currency configuration
export function getCurrencyConfig(currencyCode?: string): CurrencyConfig {
  const currency = currencyCode || getCurrentCurrency();
  const config = CURRENCY_CONFIGS[currency.toUpperCase()];

  if (!config) {
    console.warn(
      `Currency configuration not found for: ${currency}. Falling back to BRL.`
    );
    return CURRENCY_CONFIGS.BRL;
  }

  return config;
}

// Format currency value
export function formatCurrency(
  amount: number,
  currencyCode?: string,
  options?: Intl.NumberFormatOptions
): string {
  const config = getCurrencyConfig(currencyCode);

  const defaultOptions: Intl.NumberFormatOptions = {
    style: "currency",
    currency: config.code,
    ...options,
  };

  const formatted = new Intl.NumberFormat(config.locale, defaultOptions).format(amount);
  
  // Replace $ with US$ for USD currency
  if (config.code === "USD") {
    return formatted.replace("$", "US$");
  }
  
  return formatted;
}

// Format currency from cents (for payment processing)
export function formatCurrencyFromCents(
  cents: number,
  currencyCode?: string,
  options?: Intl.NumberFormatOptions
): string {
  return formatCurrency(cents / 100, currencyCode, options);
}

// Get currency symbol
export function getCurrencySymbol(currencyCode?: string): string {
  return getCurrencyConfig(currencyCode).symbol;
}

// Check if currency uses cents (most do, but some don't)
export function usesCents(currencyCode?: string): boolean {
  // Most currencies use cents/centavos, but some like JPY don't
  const config = getCurrencyConfig(currencyCode);
  return !["JPY", "KRW"].includes(config.code);
}

// Convert to payment provider format
export function toPaymentAmount(amount: number, currencyCode?: string): number {
  const config = getCurrencyConfig(currencyCode);
  // Most payment providers expect amounts in cents
  return usesCents(config.code) ? Math.round(amount * 100) : Math.round(amount);
}

// Convert from payment provider format
export function fromPaymentAmount(
  amount: number,
  currencyCode?: string
): number {
  const config = getCurrencyConfig(currencyCode);
  return usesCents(config.code) ? amount / 100 : amount;
}
